<template>
    <div class="ui-page">
        <ui-page-header title="Juízes e varas"
            subtitle="Dois cadastros ligados pela lotação: o juiz carrega o histórico de varas; a vara, o de titulares. O relacionamento da BeJ segue o juiz.">
            <v-btn v-if="$allowed('prospect.merge')" outlined @click="mergeOpen = true">
                <v-icon left small>mdi-call-merge</v-icon>Juntar duplicados<span v-if="mergeCount">&nbsp;({{ mergeCount }})</span>
            </v-btn>
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openCreate"><v-icon left small>mdi-plus</v-icon>{{ tab === "judges" ? "Novo juiz" : "Nova vara" }}</v-btn>
        </ui-page-header>

        <ui-section v-if="alerts.length" title="Alertas" description="Novos casos com juízes que a BeJ já conhece e trocas de titular detectadas na captação." class="mb-5">
            <div v-for="alert in alerts" :key="alert.id" class="pj-alert">
                <ui-badge :status="alert.type === 'known_judge' ? 'approved' : 'pending'" :text="alert.type === 'known_judge' ? 'Juiz conhecido' : 'Troca de titular'" />
                <div class="pj-alert__text">
                    <div>{{ alert.message }}</div>
                    <div class="ui-small ui-muted">{{ formatDateTime(alert.created_at) }}</div>
                </div>
                <div class="pj-alert__actions">
                    <v-btn v-if="alert.case" x-small outlined :to="`/prospeccao/casos/${alert.case.id}`">Abrir caso</v-btn>
                    <v-btn v-if="(alert.type === 'known_judge' && alert.case) && $allowed('edit')" x-small outlined :disabled="busy" @click="resolve(alert, 'assume')">Assumir</v-btn>
                    <v-btn v-if="alert.type === 'judge_change' && alert.payload.previous_judge_id" x-small outlined :to="`/prospeccao/juizes/${alert.payload.previous_judge_id}`">Atualizar lotação</v-btn>
                    <v-btn x-small text :disabled="busy" @click="resolve(alert, 'done')">Resolvido</v-btn>
                    <v-btn x-small text :disabled="busy" @click="resolve(alert, 'dismissed')">Dispensar</v-btn>
                </div>
            </div>
        </ui-section>

        <div class="ui-list-bar">
            <ui-pills v-model="tab" :items="tabItems" />
        </div>

        <ui-table-card clearable :can-clear="!!search || !!uf || status !== 'active'" @clear="search = ''; uf = null; status = 'active'; load()">
            <template #toolbar>
                <v-text-field v-model="search" :placeholder="tab === 'judges' ? 'Buscar juiz' : 'Buscar vara ou cidade'" dense outlined hide-details prepend-inner-icon="mdi-magnify"
                    class="ui-toolbar__search" @input="debouncedLoad"></v-text-field>
                <v-select v-model="uf" :items="ufs" label="UF" dense outlined hide-details clearable style="max-width: 110px;" :menu-props="{ offsetY: true }" @change="load"></v-select>
                <v-select v-model="status" :items="statusItems" dense outlined hide-details style="max-width: 140px;" :menu-props="{ offsetY: true }" @change="load"></v-select>
            </template>

            <v-data-table v-if="tab === 'judges'" :headers="judgeHeaders" :items="judges" :loading="loading" disable-sort hide-default-footer :items-per-page="-1"
                no-data-text="Nenhum juiz cadastrado" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openJudge">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 200px;">{{ item.name }} <ui-badge v-if="!item.active" status="inactive" text="inativo" /></div>
                    <div class="ui-cell-sub">{{ item.tribunal || "—" }}<span v-if="item.previous.length"> · {{ item.previous.length + item.current.length }} lotações registradas</span></div>
                </template>
                <template v-slot:[`item.current`]="{ item }">
                    <div v-for="assignment in item.current" :key="assignment.id">
                        {{ assignment.court ? assignment.court.name : "—" }}
                        <ui-badge v-if="assignment.status === 'to_confirm'" status="pending" text="a confirmar" />
                    </div>
                    <span v-if="!item.current.length" class="ui-muted">a confirmar</span>
                </template>
                <template v-slot:[`item.appointed`]="{ item }">
                    <span v-if="!item.appointed.length" class="ui-muted">—</span>
                    <div v-for="entry in item.appointed.slice(0, 3)" :key="`${entry.administrator.id}-${entry.role}`" :class="{ 'pj-house': entry.administrator.is_house }">
                        {{ entry.administrator.name }}{{ entry.role === "pp" ? " (PP)" : "" }} · {{ entry.count }}
                    </div>
                </template>
                <template v-slot:[`item.level`]="{ item }"><ui-badge :status="levelBadge(item.level)" :text="item.level_label" /></template>
                <template v-slot:[`item.last_meeting`]="{ item }">
                    <span v-if="item.last_meeting">{{ formatFeedDate(item.last_meeting.occurred_at) }}</span>
                    <span v-else class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>

            <v-data-table v-else :headers="courtHeaders" :items="courts" :loading="loading" disable-sort hide-default-footer :items-per-page="-1"
                no-data-text="Nenhuma vara cadastrada" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openCourt">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 220px;">{{ item.name }} <ui-badge v-if="!item.active" status="inactive" text="inativa" /></div>
                    <div class="ui-cell-sub">{{ [item.city, item.uf].filter(Boolean).join("/") }} · {{ [item.tribunal, item.system].filter(Boolean).join(" · ") || "tribunal não informado" }}</div>
                </template>
                <template v-slot:[`item.current`]="{ item }">
                    <div v-for="assignment in item.current" :key="assignment.id">
                        <router-link :to="`/prospeccao/juizes/${assignment.judge_id}`" @click.native.stop>{{ assignment.judge_name }}</router-link>
                        <ui-badge v-if="assignment.status === 'to_confirm'" status="pending" text="a confirmar" />
                    </div>
                    <span v-if="!item.current.length" class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.previous`]="{ item }">
                    <div v-for="assignment in item.previous" :key="assignment.id" class="ui-small">
                        {{ assignment.judge_name }}<span v-if="assignment.ended_at" class="ui-muted"> (até {{ formatFeedDate(assignment.ended_at) }})</span>
                    </div>
                    <span v-if="!item.previous.length" class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.contact`]="{ item }">
                    <span class="ui-small" :class="{ 'ui-muted': !item.email && !item.phone && !item.clerk_name }">
                        {{ [item.email, item.phone, item.clerk_name ? `assessor(a) ${item.clerk_name}` : null].filter(Boolean).join(" · ") || "a preencher" }}
                    </span>
                </template>
                <template v-slot:[`item.appointed`]="{ item }">
                    <span v-if="!item.appointed.length" class="ui-muted">—</span>
                    <div v-for="entry in item.appointed" :key="entry.name" :class="{ 'pj-house': entry.is_house }">{{ entry.name }} · {{ entry.count }}</div>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
        </ui-table-card>
        <p class="ui-small ui-muted mt-2">A captação cria sozinha o juiz e a vara que ainda não existem; o cadastro manual serve para o que a equipe sabe antes. Ao captar um caso, o sistema cruza o juiz com o cadastro: se houve agenda, onepage ou nomeação da BeJ com ele em qualquer vara, gera o alerta. Se a vara aparece com outro juiz, registra a troca de titular a confirmar.</p>

        <judge-form-dialog v-model="judgeDialog" :courts="courtOptions" @saved="(judge) => $router.push(`/prospeccao/juizes/${judge.id}`)" />
        <court-form-dialog v-model="courtDialog.open" :court="courtDialog.court" @saved="afterCourtSave" />
        <bench-merge-dialog v-model="mergeOpen" :kind="tab === 'judges' ? 'judge' : 'court'" :items="tab === 'judges' ? judges : courts" @merged="load" @count="(count) => mergeCount = count" />
    </div>
</template>

<script>
import axios from "axios";
import JudgeFormDialog from "@/components/prospect/bench/JudgeFormDialog.vue";
import CourtFormDialog from "@/components/prospect/bench/CourtFormDialog.vue";
import BenchMergeDialog from "@/components/prospect/bench/BenchMergeDialog.vue";
import { formatDateTime, formatFeedDate, notifyError, notifySuccess } from "@/components/prospect/prospect.util";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];

export default {
    name: "ProspectJudges",

    components: { JudgeFormDialog, CourtFormDialog, BenchMergeDialog },

    data: () => ({
        tab: "judges",
        judges: [],
        courts: [],
        alerts: [],
        loading: false,
        busy: false,
        search: "",
        uf: null,
        ufs: UFS,
        status: "active",
        statusItems: [{ value: "active", text: "Ativos" }, { value: "inactive", text: "Inativos" }, { value: "all", text: "Todos" }],
        judgeDialog: false,
        courtDialog: { open: false, court: null },
        courtOptions: [],
        mergeOpen: false,
        mergeCount: 0,
        timer: null,
        judgeHeaders: [
            { text: "Juiz(a)", value: "name" },
            { text: "Lotação atual", value: "current" },
            { text: "Casos", value: "cases", width: 80 },
            { text: "AJs que nomeou", value: "appointed" },
            { text: "Relação BeJ", value: "level", width: 150 },
            { text: "Última agenda", value: "last_meeting", width: 120 },
            { text: "", value: "actions", align: "end", width: 40 },
        ],
        courtHeaders: [
            { text: "Vara", value: "name" },
            { text: "Titular atual", value: "current" },
            { text: "Titulares anteriores", value: "previous" },
            { text: "Contato do gabinete", value: "contact" },
            { text: "Casos", value: "cases", width: 80 },
            { text: "AJs nomeados", value: "appointed" },
            { text: "", value: "actions", align: "end", width: 40 },
        ],
    }),

    computed: {
        tabItems() {
            return [{ value: "judges", text: "Juízes (pessoas)", count: this.judges.length }, { value: "courts", text: "Juízos (varas)", count: this.courts.length }];
        },
    },

    watch: {
        tab() {
            this.load();
            this.loadMergeCount();
        },
    },

    created() {
        this.load();
        this.loadAlerts();
        this.loadMergeCount();
    },

    methods: {
        formatDateTime,
        formatFeedDate,

        levelBadge(level) {
            return { nomeou_bej: "approved", agenda_feita: "active", contato: "pending" }[level] || "inactive";
        },

        async load() {
            this.loading = true;
            try {
                const params = {};
                if (this.search) params.search = this.search;
                if (this.uf) params.uf = this.uf;
                if (this.status !== "active") params.status = this.status;
                const result = await axios.get(this.tab === "judges" ? "/api/admin/prospect/judges" : "/api/admin/prospect/courts", { params });
                if (this.tab === "judges") this.judges = result.data.content;
                else this.courts = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async loadAlerts() {
            try {
                const result = await axios.get("/api/admin/prospect/alerts");
                this.alerts = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        debouncedLoad() {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => this.load(), 350);
        },

        async loadMergeCount() {
            try {
                const result = await axios.get(this.tab === "judges" ? "/api/admin/prospect/judges/merge-suggestions" : "/api/admin/prospect/courts/merge-suggestions");
                this.mergeCount = result.data.content.length;
            } catch (error) {
                this.mergeCount = 0;
            }
        },

        async openCreate() {
            if (this.tab === "courts") {
                this.courtDialog = { open: true, court: null };
                return;
            }
            // Varas ativas para a lotacao inicial do juiz.
            try {
                const result = await axios.get("/api/admin/prospect/courts");
                this.courtOptions = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
            this.judgeDialog = true;
        },

        openCourt(item) {
            this.courtDialog = { open: true, court: item };
        },

        afterCourtSave() {
            this.load();
            this.loadMergeCount();
        },

        openJudge(item) {
            this.$router.push(`/prospeccao/juizes/${item.id}`);
        },

        async resolve(alert, action) {
            this.busy = true;
            try {
                await axios.post(`/api/admin/prospect/alerts/${alert.id}/resolve`, { action });
                notifySuccess(this, action === "assume" ? "Você é o dono do caso." : "Alerta resolvido.");
                await this.loadAlerts();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pj-alert {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid $ui-border-soft;
    font-size: 13px;

    &:last-child {
        border-bottom: 0;
    }
}

.pj-alert__text {
    flex: 1 1 auto;
}

.pj-alert__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
    max-width: 320px;
}

.pj-house {
    color: $ui-accent;
    font-weight: 600;
}
</style>
